import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/auth.store";
import type { StorageHealthReport, StorageRepairMode, StorageRepairResult } from "@shared";

// ═══════════════════════════════════════════════════════════
// Storage Health Hooks — TanStack Query wrappers for
// /api/admin/storage/health and /api/admin/storage/repair
// ═══════════════════════════════════════════════════════════

/**
 * Fetch the current Data Storage health report.
 * staleTime: 0 — always re-fetches (diagnostic read).
 * Only enabled for admin users.
 */
export function useStorageHealth() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const memberId = useAuthStore((s) => s.memberId);
  return useQuery({
    queryKey: queryKeys.storageHealth,
    queryFn: () => api.get<StorageHealthReport>("/api/admin/storage/health"),
    enabled: !!isAdmin && !!memberId,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  });
}

/**
 * Trigger a storage repair operation.
 * On success the query cache for storageHealth is updated with
 * the post-repair report returned by the server.
 */
export function useRepairStorage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mode: StorageRepairMode) =>
      api.post<StorageRepairResult>("/api/admin/storage/repair", { mode }),
    onSuccess: (result) => {
      // Update the cached health report immediately with the post-repair state
      queryClient.setQueryData(queryKeys.storageHealth, result.report);
      // Invalidate settings so the storageSchemaVersion field refreshes
      // (dismisses the StorageSchemaBanner when repair succeeds)
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}
